import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Box - Podstawowy komponent layoutu w stylu Radix Themes
 * 
 * API zgodne z @radix-ui/themes
 * - Warianty: nie ma (Box jest neutralny)
 * - Padding/margin z 8px scale (0-9)
 * - Display modes: block/flex/grid
 * 
 * @example
 * ```tsx
 * <Box p="4" mb="6">
 *   <Text>Zawartość</Text>
 * </Box>
 * ```
 */

const boxVariants = cva(
  "transition-colors",
  {
    variants: {
      // Box w Radix nie ma wariantów kolorystycznych - jest neutralny
      // Używaj Card dla styled containers
      variant: {
        ghost: "",
      },
      // Padding scale (8px)
      p: {
        "0": "p-0",
        "1": "p-[var(--space-1)]",
        "2": "p-[var(--space-2)]",
        "3": "p-[var(--space-3)]",
        "4": "p-[var(--space-4)]",
        "5": "p-[var(--space-5)]",
        "6": "p-[var(--space-6)]",
        "7": "p-[var(--space-7)]",
        "8": "p-[var(--space-8)]",
        "9": "p-[var(--space-9)]",
      },
      // Margin scale
      m: {
        "0": "m-0",
        "1": "m-[var(--space-1)]",
        "2": "m-[var(--space-2)]",
        "3": "m-[var(--space-3)]",
        "4": "m-[var(--space-4)]",
        "5": "m-[var(--space-5)]",
        "6": "m-[var(--space-6)]",
        "7": "m-[var(--space-7)]",
        "8": "m-[var(--space-8)]",
        "9": "m-[var(--space-9)]",
      },
      // Display variants
      display: {
        block: "block",
        inline: "inline",
        "inline-block": "inline-block",
        flex: "flex",
        "inline-flex": "inline-flex",
        grid: "grid",
        "inline-grid": "inline-grid",
        none: "hidden",
      },
      // Border radius
      rounded: {
        none: "rounded-none",
        sm: "rounded-[var(--radius-sm)]",
        base: "rounded-[var(--radius-base)]",
        md: "rounded-[var(--radius-md)]",
        lg: "rounded-[var(--radius-lg)]",
        xl: "rounded-[var(--radius-xl)]",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "ghost",
      display: "block",
      rounded: "none",
    },
  }
)

export interface BoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof boxVariants> {
  /** Change the rendered HTML element (e.g. "section", "article", "main") */
  as?: React.ElementType
  asChild?: boolean
  /** Padding top */
  pt?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Padding bottom */
  pb?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Padding left */
  pl?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Padding right */
  pr?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Padding horizontal (left + right) */
  px?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Padding vertical (top + bottom) */
  py?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Margin top */
  mt?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Margin bottom */
  mb?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Margin left */
  ml?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Margin right */
  mr?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Margin horizontal (left + right) */
  mx?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  /** Margin vertical (top + bottom) */
  my?: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
}

const Box = React.forwardRef<HTMLDivElement, BoxProps>(
  ({ 
    className, 
    variant, 
    as = "div",
    p, 
    m, 
    pt, pb, pl, pr, px, py,
    mt, mb, ml, mr, mx, my,
    display, 
    rounded, 
    asChild = false, 
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : as
    
    // Build spacing classes
    const spacingClasses = cn(
      // Padding
      pt && `pt-[var(--space-${pt})]`,
      pb && `pb-[var(--space-${pb})]`,
      pl && `pl-[var(--space-${pl})]`,
      pr && `pr-[var(--space-${pr})]`,
      px && `px-[var(--space-${px})]`,
      py && `py-[var(--space-${py})]`,
      // Margin
      mt && `mt-[var(--space-${mt})]`,
      mb && `mb-[var(--space-${mb})]`,
      ml && `ml-[var(--space-${ml})]`,
      mr && `mr-[var(--space-${mr})]`,
      mx && `mx-[var(--space-${mx})]`,
      my && `my-[var(--space-${my})]`,
    )
    
    return (
      <Comp
        className={cn(
          boxVariants({ variant, p, m, display, rounded }), 
          spacingClasses,
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Box.displayName = "Box"

export { Box, boxVariants }

